// Session bootstrap + catalog freshness. Owner: integration lead (WP00).
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionResponse, Connection } from '@tuned/contracts';
import { api } from './api.ts';

export const sessionKey = ['session'] as const;

/** True while a first or refresh import is running (XC04: catalog appears incrementally). */
export const importing = (c: Connection) =>
  c.catalog?.status === 'importing' || c.catalog?.guide?.status === 'importing' ||
  (c.sessionState === 'connected' && c.catalog?.revision === null && c.catalog?.status !== 'failed');

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: () => api<SessionResponse>('GET', '/api/session'),
    staleTime: 60_000,
    refetchInterval: (q) => (q.state.data?.connections.some(importing) ? 3000 : false),
  });
}
/** P1: one active provider connection per workspace (multiple sources are P2, EXT01). */
export function useActiveConnection(): Connection | null {
  const { data } = useSession();
  return data?.connections.find((c) => c.sessionState === 'connected') ?? data?.connections[0] ?? null;
}
export function useInvalidateSession() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: sessionKey });
}

/** Mounted once: when a new catalog/guide snapshot is published, refetch everything derived from it (STORE02). */
export function CatalogSync() {
  const conn = useActiveConnection();
  const qc = useQueryClient();
  const key = conn ? `${conn.id}|${conn.catalog?.revision}|${conn.catalog?.guide?.lastSuccessAt}` : '';
  const prev = useRef(key);
  useEffect(() => {
    if (prev.current !== key && prev.current !== '') {
      for (const k of ['catalog', 'categories', 'catalog-more', 'guide', 'home', 'movie', 'series']) void qc.invalidateQueries({ queryKey: [k] });
    }
    prev.current = key;
  }, [key, qc]);
  return null;
}
